/**
 * CiaoChat — Centered modal chat
 * Clean, minimal, professional
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { IoClose, IoAdd, IoExpand, IoContract } from 'react-icons/io5';
import CiaoMessage from './CiaoMessage';
import CiaoInput from './CiaoInput';
import CiaoTypingIndicator from './CiaoTypingIndicator';
import CiaoConfirmation from './CiaoConfirmation';
import CiaoWarningModal from './CiaoWarningModal';
import CiaoExpertSelector from './CiaoExpertSelector';
import CiaoConnecting from './CiaoConnecting';
import {
  createChatSession, sendMessage, updateSessionSettings, confirmAction, getExperts,
  type ChatMessage, type PendingConfirmation, type ExpertInfo,
} from '../modules/ciaoApi';

interface CiaoChatProps {
  onClose: () => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

/** Get current user's first name from localStorage */
function getUserFirstName(): string {
  try {
    const raw = localStorage.getItem('user');
    if (raw) {
      const u = JSON.parse(raw);
      return u.username || u.name || '';
    }
  } catch { /* ignore */ }
  return '';
}

interface ChatMessageWithAttachments extends ChatMessage {
  attachments?: { type: string; name: string; pages?: number; preview?: string }[];
}

const CiaoChat: React.FC<CiaoChatProps> = ({ onClose, isExpanded, onToggleExpand }) => {
  const [messages, setMessages] = useState<ChatMessageWithAttachments[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [autoExecuteWrites, setAutoExecuteWrites] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [experts, setExperts] = useState<ExpertInfo[]>([]);
  const [selectedExpertId, setSelectedExpertId] = useState<string>('auto');
  const userName = useMemo(() => getUserFirstName(), []);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, isLoading, pendingConfirmation, scrollToBottom]);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        // Load experts and create session in parallel
        const [session, expertList] = await Promise.all([
          createChatSession(),
          getExperts(),
        ]);
        if (!cancelled) {
          setSessionId(session.sessionId);
          setExperts(expertList);
          setError(null);
        }
      } catch (err: any) {
        if (!cancelled) {
          console.error('[Ciao] Session init failed:', err);
          setError('No se pudo conectar con Ciao. Verifica que el servidor esté activo.');
        }
      } finally {
        if (!cancelled) setIsInitializing(false);
      }
    };
    init();
    return () => { cancelled = true; };
  }, []);

  const handleSend = async (text: string, files?: File[]) => {
    if (!sessionId || isLoading) return;

    // Build attachment metadata for display (create previews for images)
    const attachments = files?.map((f) => ({
      type: f.type.startsWith('image/') ? 'image' : 'pdf',
      name: f.name,
      preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : undefined,
    }));

    const userMsg: ChatMessageWithAttachments = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
      attachments,
    };
    setMessages((prev) => [...prev, userMsg]);
    setIsLoading(true);
    setError(null);
    setPendingConfirmation(null);

    try {
      const effectiveExpertId = selectedExpertId !== 'auto' ? selectedExpertId : undefined;
      const response = await sendMessage(sessionId, text, files, effectiveExpertId);
      const expertMeta = experts.find(e => e.id === response.agent);
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: response.message,
        timestamp: new Date().toISOString(),
        agentName: expertMeta?.displayName || response.agent,
      }]);

      if (response.pendingConfirmation) {
        setPendingConfirmation(response.pendingConfirmation);
      }
    } catch (err: any) {
      const errorMsg = err?.message || 'Error desconocido';
      console.error('[Ciao] Send error:', errorMsg);
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: `Error al procesar tu mensaje: ${errorMsg}`,
        timestamp: new Date().toISOString(),
        agentName: 'Sistema',
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleAutoExecute = () => {
    if (!autoExecuteWrites) {
      // Turning ON → show warning modal
      setShowWarningModal(true);
    } else {
      // Turning OFF → immediate
      setAutoExecuteWrites(false);
      if (sessionId) {
        updateSessionSettings(sessionId, { autoExecuteWrites: false }).catch(console.error);
      }
    }
  };

  const handleWarningConfirm = () => {
    setShowWarningModal(false);
    setAutoExecuteWrites(true);
    if (sessionId) {
      updateSessionSettings(sessionId, { autoExecuteWrites: true }).catch(console.error);
    }
  };

  const handleWarningCancel = () => {
    setShowWarningModal(false);
  };

  const handleConfirmAction = async (action: 'accept' | 'cancel') => {
    if (!sessionId || !pendingConfirmation || isConfirming) return;
    setIsConfirming(true);

    try {
      const response = await confirmAction(sessionId, pendingConfirmation.id, action);
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: response.message,
        timestamp: new Date().toISOString(),
        agentName: response.agent || 'Sistema',
      }]);
    } catch (err: any) {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: `Error: ${err.message}`,
        timestamp: new Date().toISOString(),
        agentName: 'Sistema',
      }]);
    } finally {
      setPendingConfirmation(null);
      setIsConfirming(false);
    }
  };

  const handleNewChat = async () => {
    setIsInitializing(true);
    setMessages([]);
    setError(null);
    setPendingConfirmation(null);
    try {
      const session = await createChatSession();
      setSessionId(session.sessionId);
      // Re-apply auto-execute setting to new session
      if (autoExecuteWrites) {
        updateSessionSettings(session.sessionId, { autoExecuteWrites: true }).catch(console.error);
      }
    } catch {
      setError('No se pudo crear una nueva conversación.');
    } finally {
      setIsInitializing(false);
    }
  };

  const inputDisabled = isLoading || isInitializing || !sessionId || isConfirming;

  // Current expert metadata for greeting + quick actions
  const currentExpert = experts.find(e => e.id === selectedExpertId) || experts[0];
  const greetingText = currentExpert
    ? currentExpert.greeting.replace('{name}', userName || 'usuario')
    : '';
  const quickActions = currentExpert?.quickActions || [];
  const isWelcomeScreen = messages.length === 0 && !isLoading && sessionId;

  return (
    <>
      <div
        className="ciao-animate-scale-in"
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: isExpanded ? '100%' : 520,
          height: isExpanded ? '100%' : '80vh',
          maxHeight: isExpanded ? '100%' : 700,
          minHeight: isExpanded ? '100%' : 400,
          background: 'var(--background-color, #fff)',
          borderRadius: isExpanded ? 0 : 16,
          boxShadow: isExpanded ? 'none' : '0 25px 60px rgba(0,0,0,0.2), 0 8px 24px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          transition: 'max-width 0.25s ease, border-radius 0.25s ease, max-height 0.25s ease',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            background: '#FC7556',
            color: '#fff',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 32, height: 32, borderRadius: 10,
                background: 'rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 15,
              }}
            >
              ✦
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: '0.02em', lineHeight: 1.2 }}>Ciao</span>
                <span style={{
                  fontSize: 8,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase' as const,
                  background: 'rgba(255,255,255,0.25)',
                  color: '#fff',
                  padding: '2px 6px',
                  borderRadius: 4,
                  lineHeight: 1.3,
                }}>
                  Beta
                </span>
              </div>
              <div style={{ fontSize: 10, opacity: 0.85, fontWeight: 500, lineHeight: 1.2 }}>Asistente IA de MedioPunto</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Auto-execute toggle */}
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 5, marginRight: 6 }}
              title={autoExecuteWrites
                ? 'Modo directo: las acciones se ejecutan sin confirmación'
                : 'Modo seguro: las acciones requieren confirmación'}
            >
              <span style={{ fontSize: 9, opacity: 0.8, fontWeight: 500, whiteSpace: 'nowrap' }}>
                {autoExecuteWrites ? 'Directo' : 'Seguro'}
              </span>
              <button
                onClick={handleToggleAutoExecute}
                style={{
                  position: 'relative',
                  width: 32,
                  height: 18,
                  borderRadius: 9,
                  background: autoExecuteWrites ? 'rgba(245,158,11,0.9)' : 'rgba(255,255,255,0.25)',
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: 2,
                    left: autoExecuteWrites ? 16 : 2,
                    width: 14,
                    height: 14,
                    borderRadius: 7,
                    background: '#fff',
                    transition: 'left 0.2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  }}
                />
              </button>
            </div>
            <button
              onClick={handleNewChat}
              style={{
                width: 30, height: 30, borderRadius: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', background: 'transparent',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              title="Nueva conversación"
            >
              <IoAdd size={18} />
            </button>
            <button
              onClick={onToggleExpand}
              style={{
                width: 30, height: 30, borderRadius: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', background: 'transparent',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              title={isExpanded ? 'Contraer' : 'Expandir'}
            >
              {isExpanded ? <IoContract size={16} /> : <IoExpand size={16} />}
            </button>
            <button
              onClick={onClose}
              style={{
                width: 30, height: 30, borderRadius: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', background: 'transparent',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              title="Cerrar"
            >
              <IoClose size={18} />
            </button>
          </div>
        </div>

        {/* Beta disclaimer */}
        <div style={{
          padding: '6px 16px',
          background: 'rgba(252, 117, 86, 0.06)',
          borderBottom: '1px solid rgba(252, 117, 86, 0.1)',
          fontSize: 10,
          lineHeight: 1.5,
          color: 'var(--text-color-muted, #888)',
          textAlign: 'center',
          flexShrink: 0,
        }}>
          Ciao puede cometer errores. Verifica la información importante antes de tomar acción.
        </div>

        {/* Error bar */}
        {error && (
          <div style={{
            padding: '8px 16px', background: 'rgba(220,53,69,0.08)', color: '#dc3545',
            fontSize: 12, textAlign: 'center', flexShrink: 0,
          }}>
            {error}
            <button
              onClick={handleNewChat}
              style={{ marginLeft: 8, textDecoration: 'underline', color: '#dc3545', fontSize: 12, background: 'none', cursor: 'pointer' }}
            >
              Reintentar
            </button>
          </div>
        )}

        {/* Messages area */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '16px 16px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Connecting animation */}
          {isInitializing && <CiaoConnecting />}

          {/* Welcome screen — animated greeting + quick actions */}
          {isWelcomeScreen && (
            <div
              className="ciao-animate-fade-in"
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                gap: 20,
                padding: '20px 12px',
              }}
            >
              {/* Animated avatar */}
              <div className="ciao-avatar-pulse" style={{
                width: 56, height: 56, borderRadius: 16,
                background: 'linear-gradient(135deg, #FC7556, #ff9a76)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 26, color: '#fff',
                boxShadow: '0 4px 20px rgba(252, 117, 86, 0.25)',
              }}>
                ✦
              </div>

              {/* Greeting */}
              <div>
                <div className="ciao-typing-greeting" style={{
                  fontSize: 15, fontWeight: 600,
                  color: 'var(--text-color, #333)', lineHeight: 1.5,
                  maxWidth: 320,
                }}>
                  {greetingText}
                </div>
              </div>

              {/* Quick actions */}
              {quickActions.length > 0 && (
                <div style={{
                  display: 'flex', flexDirection: 'column', gap: 6,
                  width: '100%', maxWidth: 320,
                }}>
                  {quickActions.map((action, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSend(action)}
                      className="ciao-animate-fade-in"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '10px 14px', borderRadius: 12,
                        background: 'var(--card-color, #f8f8f8)',
                        color: 'var(--text-color, #444)',
                        fontSize: 12, fontWeight: 500,
                        textAlign: 'left', lineHeight: 1.4,
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        border: '1px solid rgba(0,0,0,0.04)',
                        animationDelay: `${0.1 + idx * 0.08}s`,
                        animationFillMode: 'both',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(252, 117, 86, 0.06)';
                        e.currentTarget.style.borderColor = 'rgba(252, 117, 86, 0.2)';
                        e.currentTarget.style.color = '#FC7556';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--card-color, #f8f8f8)';
                        e.currentTarget.style.borderColor = 'rgba(0,0,0,0.04)';
                        e.currentTarget.style.color = 'var(--text-color, #444)';
                      }}
                    >
                      <span style={{
                        flexShrink: 0, width: 24, height: 24, borderRadius: 8,
                        background: 'rgba(252, 117, 86, 0.1)', color: '#FC7556',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700,
                      }}>
                        {currentExpert?.icon || '→'}
                      </span>
                      {action}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Chat messages */}
          {messages.map((msg, idx) => (
            <CiaoMessage
              key={idx}
              role={msg.role}
              content={msg.content}
              timestamp={msg.timestamp}
              agentName={msg.agentName}
              isLatest={idx === messages.length - 1 && !pendingConfirmation}
              isExpanded={isExpanded}
              attachments={msg.attachments}
            />
          ))}

          {/* Pending confirmation widget */}
          {pendingConfirmation && !isLoading && (
            <CiaoConfirmation
              confirmation={pendingConfirmation}
              onAccept={() => handleConfirmAction('accept')}
              onCancel={() => handleConfirmAction('cancel')}
              isProcessing={isConfirming}
            />
          )}

          {isLoading && <CiaoTypingIndicator />}
          <div ref={messagesEndRef} />
        </div>

        {/* Expert Selector + Input */}
        <div style={{ flexShrink: 0, borderTop: '1px solid rgba(128,128,128,0.15)' }}>
          <div style={{ display: 'flex', alignItems: 'center', padding: '8px 14px 2px', gap: 8 }}>
            {experts.length > 0 && (
              <CiaoExpertSelector
                experts={experts}
                selectedExpertId={selectedExpertId}
                onSelect={setSelectedExpertId}
                disabled={inputDisabled}
              />
            )}
          </div>
          <CiaoInput
            onSend={handleSend}
            disabled={inputDisabled}
            placeholder={
              isInitializing ? 'Conectando...'
                : !sessionId ? 'Sin conexión — haz clic en Reintentar'
                : isConfirming ? 'Procesando acción...'
                : 'Pregúntale algo a Ciao...'
            }
          />
        </div>
      </div>

      {/* Warning Modal */}
      {showWarningModal && (
        <CiaoWarningModal
          onConfirm={handleWarningConfirm}
          onCancel={handleWarningCancel}
        />
      )}
    </>
  );
};

export default CiaoChat;
